import React, { useRef, useEffect } from "react";
import history from '../../history';
import "./style.css";
import BotImage from "./assets/VIA3.png";
import connect from "react-redux/es/connect/connect";

const ChatBot = ({
  navStack,
  setNavStack,
  setCurrentNode,
  currentNode,
  messages,
  setMessages,
  loading,
  setLoading,
  tree,
  pageContent
}) => {
  const messagesEndRef = useRef(null);

  const getTitleFromHtml = (htmlContent) => {
    const div = document.createElement("div");
    div.innerHTML = htmlContent;
    return div.textContent;
  };

  const sanitizeMessageContent = (content = "") => {
    if (!content || typeof content !== "string") return "";

    const container = document.createElement("div");
    container.innerHTML = content.trim();

    const removeEmptyElements = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === Node.ELEMENT_NODE) {
          removeEmptyElements(child);
        }
      });

      if (node.nodeType === Node.ELEMENT_NODE) {
        const hasText = (node.textContent || "").trim();
        const hasChildren = node.children && node.children.length > 0;

        if (!hasText && !hasChildren) {
          node.remove();
        }
      }
    };

    removeEmptyElements(container);
    return container.innerHTML;
  };

  // const getAmiMessage = (htmlContent) => {
  //   const div = document.createElement("div");
  //   div.innerHTML = htmlContent;
  //   const beforeAmiMessage = pageContent.content.page.amiAssistant.beforeAmiMessage||"";
  //   const afterAmiMessage = pageContent.content.page.amiAssistant.afterAmiMessage||"";
  //   return (
  //     beforeAmiMessage + `<strong>${div.textContent}</strong>` + afterAmiMessage
  //   );
  // };

  // Helper to check if a node has at least one valid custom message
  const hasAnyValidMessage = (node) => {
    if (!node.messages) return false;
    const stripHtml = (html) => {
      const tmp = document.createElement("DIV");
      tmp.innerHTML = html || "";
      return (tmp.textContent || tmp.innerText || "").trim();
    };
    return stripHtml(node.messages.userMessage) || stripHtml(node.messages.amiMessage);
  };

  // Helper to check which messages are present
  const getMessageStatus = (node) => {
    if (!node.messages) return { hasUser: false, hasAmi: false };
    const stripHtml = (html) => {
      const tmp = document.createElement("DIV");
      tmp.innerHTML = html || "";
      return (tmp.textContent || tmp.innerText || "").trim();
    };
    return {
      hasUser: stripHtml(node.messages.userMessage),
      hasAmi: stripHtml(node.messages.amiMessage)
    };
  };

  const formatTime = (date) =>
    date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const findNodeById = (nodeId, nodes = tree) => {
    for (const node of nodes) {
      if (node.id === nodeId) return node;
      if (node.children && node.children.length) {
        const found = findNodeById(nodeId, node.children);
        if (found) return found;
      }
    }
    return null;
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Intercept clicks on relative/internal links inside messages to use SPA navigation
  useEffect(() => {
    const container = messagesEndRef.current ? messagesEndRef.current.parentElement : null;
    if (!container) return;

    const onClick = (e) => {
      // Find the closest anchor
      const a = e.target.closest && e.target.closest('a');
      if (!a) return;

      const href = a.getAttribute('href');
      // Only handle relative/internal links (start with / and not full http(s) or mailto/tel)
      if (!href) return;
      const isExternal = /^(?:[a-zA-Z][a-zA-Z0-9+.-]*:)?\/\//.test(href) || href.startsWith('mailto:') || href.startsWith('tel:');
      const isHashOnly = href.startsWith('#');
      if (isExternal || isHashOnly) {
        // Let the browser handle external links and hashes
        return;
      }

      // Relative or absolute-path internal navigation
      if (href.startsWith('/') || !/^[a-zA-Z]+:/.test(href)) {
        e.preventDefault();
        try {
          // Use history to navigate within the SPA without hard refresh
          history.push(href);
        } catch (err) {
          // Fallback to location assign if history isn't available for some reason
          window.location.assign(href);
        }
      }
    };

    container.addEventListener('click', onClick);
    return () => container.removeEventListener('click', onClick);
  }, [messagesEndRef, messages]);

  // User picks a node option
  const handleSelectNode = (nodeId) => {
    const node = findNodeById(nodeId);
    if (!node) return;
    setLoading(true);
    setNavStack((stack) => stack.concat(currentNode));
    setTimeout(() => {
      const messageStatus = getMessageStatus(node);
      const hasCustomMessages = messageStatus.hasUser || messageStatus.hasAmi;

      const newMessages = [];

      if (hasCustomMessages) {
        // Use custom messages when at least one is present
        if (messageStatus.hasUser) {
          newMessages.push({
            type: "user",
            content: `You: ${node.messages.userMessage}`.trim(),
            time: formatTime(new Date()),
          });
        }
        if (messageStatus.hasAmi) {
          newMessages.push({
            type: "bot",
            content: `Ami: ${node.messages.amiMessage}`.trim(),
            time: formatTime(new Date()),
          });
        }
      } else {
        // No custom messages - use htmlContent for both
        newMessages.push(
          {
            type: "user",
            content: `You: ${getTitleFromHtml(node.htmlContent).trim()}`,
            time: formatTime(new Date()),
          },
          {
            type: "bot",
            content: `Ami: ${node.htmlContent}`.trim(),
            time: formatTime(new Date()),
          }
        );
      }

      setMessages((msgs) => msgs.concat(newMessages));
      setCurrentNode(node);
      setLoading(false);
    }, 400);
  };

  // Show all children without filtering
  const visibleChildren = currentNode.children || [];
  const childChoices = visibleChildren.length > 0;

  return (
    <div className="chatbot-ui">
      <div style={{ flex: 1, overflowY: "auto", padding: "1em", fontWeight: "bold" }}>
        {messages.map((msg, i) => (
          <div
            className={
              msg.type === "user"
                ? "message user-message"
                : "message bot-message"
            }
            key={i}
          >
            <div className="message-content">
              {msg.type === "bot" && (
                <span
                  className="bot-icon"
                  style={{
                    marginRight: "8px",
                    display: "inline-flex",
                    verticalAlign: "middle",
                  }}
                >
                  <img
                    src={BotImage}
                    alt="Bot"
                    style={{
                      width: "1.5rem",
                      height: "1.5rem",
                      boxShadow: "none",
                    }}
                  />
                </span>
              )}
              {msg.type === "user" && (
                <span
                  className="user-icon"
                  style={{
                    marginRight: "9px",
                    display: "inline-flex",
                    verticalAlign: "middle",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="19"
                    height="19"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0" />
                    <path
                      fillRule="evenodd"
                      d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8m8-7a7 7 0 0 0-5.468 11.37C3.242 11.226 4.805 10 8 10s4.757 1.225 5.468 2.37A7 7 0 0 0 8 1"
                    />
                  </svg>
                </span>
              )}
              <span
                className="message-text"
                dangerouslySetInnerHTML={{ __html: sanitizeMessageContent(msg.content) }}
              />
            </div>
            <span className="message-time">{msg.time}</span>
          </div>
        ))}

        <div ref={messagesEndRef} />
      </div>
      {loading && <div className="loading-indicator">Loading...</div>}
      <div className="choices-wrapper">
        {childChoices ? (
          <div className="choices-grid">
            {visibleChildren.map((child) => (
              <button
                key={child.id}
                className="choice-btn"
                onClick={() => handleSelectNode(child.id)}
              >
                {getTitleFromHtml(child.htmlContent)}
              </button>
            ))}
          </div>
        ) : (
          <div style={{ opacity: 0.65, textAlign: "center", color: "#626C71" }}>
            You've reached the end of this section. Use the navigation buttons
            above to explore other areas.
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  pageContent: state.pageContent,
});

export default connect(mapStateToProps)(ChatBot);



// WEBPACK FOOTER //
// ./src/mi-app/AMI_assistant/ChatBot.js